<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-2023 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher, onMount } from 'svelte';
    import { __, visibilityObserver, generateElementId } from '@oat-sa-private/ui-core';
    import { HeaderBar, Panel, NotificationContainer } from '@oat-sa-private/ui-components';
    import Transition from './layout/Transition.svelte';
    import ItemHanger from './layout/itemHanger/ItemHanger.svelte';
    import UserMenu from './layout/userMenu/UserMenu.svelte';
    import { testSessionStatus } from './session/sessionStates.js';
    import { isItemModalFeedbackState } from './util/testContext.js';
    import { getTestSessionStatusStore, getTestStateStore } from './testsStateStore.js';
    import { setScreenSize, screenSize } from './screenSizeStore.js';
    import { getTestSessionUserDataService } from './session/testSessionUserDataService.js';
    import { getNavigationFeedbacksStore } from './feedback';
    import Overlay from './layout/overlay/Overlay.svelte';
    import FloatingToolbarAreas from './layout/FloatingToolbarAreas.svelte';
    import { createToolbarItemsApi } from './layout/toolbarItems.js';
    import resizeObserve from '@oat-sa-private/tao-item-runner-qtinui/src/runner/interactions/util/actions/resizeObserve.js';
    import FeedbackDialogsContainer from './feedback/FeedbackDialogsContainer.svelte';

    const dispatch = createEventDispatcher();

    /**
     * The test runner main layout component
     * @property {string} serviceCallId - id of test session
     * @property {Object} [theme] - contains the test runner theming information
     * @property {Object} [theme.logo] - a custom logo
     * @property {string} [theme.logo.src] - custom logo source
     * @property {string} [theme.logo.alt] - custom logo alternative text
     * @property {boolean} [theme.hideMenuButton] - hide menu button from header
     * @property {boolean} [theme.showUserMenu] - hide user menu from header
     * @property {Object?} [testTaker] - user info
     * @property {Object[]} [itemHangerMessages] - list of item hanger messages
     * @property {Object} [plugins] - plugins object, in format { pluginName: PluginAPI }
     * @property {Object} [options] - options object (part of testRunnerConfiguration)
     */
    export let serviceCallId;
    export let theme = {};
    export let testTaker;
    export let itemHangerMessages = [];
    export let plugins = {};

    const status = getTestSessionStatusStore(serviceCallId);
    const testStateStore = getTestStateStore(serviceCallId);
    const toolsStore = getTestSessionUserDataService(serviceCallId).getToolsStore();
    const navigationFeedbacksStore = getNavigationFeedbacksStore(serviceCallId);

    //list of main test runner areas
    const areas = {};

    // For header & footer scroll shadows:
    let fullyScrolledUp = true;
    let fullyScrolledDown = true;

    const panelId = generateElementId('panel');
    const a11yPanelId = generateElementId('panel');

    let menuPanelOpen = false;

    const showUserMenu = theme && theme.showUserMenu;
    const showMenuButton = theme && !theme.hideMenuButton;
    // startActions configures the buttons at the start of the HeaderBar
    /* eslint-disable indent */
    $: startActions = showMenuButton
        ? [
              {
                  key: 'menu',
                  icon: 'menu',
                  label: __('Main menu'),
                  ariaLabel: __('Main menu'),
                  dataTestId: 'mainmenu',
                  ariaHasPopup: true,
                  ariaControls: panelId,
                  ariaExpanded: menuPanelOpen
              }
          ]
        : [];
    /* eslint-enable indent */

    // plugins toolbar init + store reactivity
    const toolbarItemsApi = createToolbarItemsApi(plugins, toolsStore);
    // endActions configures the buttons at the end of the HeaderBar
    $: endActions = toolbarItemsApi.getToolbarActions($toolsStore);

    /**
     * Header Bar items click handler
     * @param {Event} event
     */
    function handleHeaderBarAction(event) {
        // forward all HeaderBar events to qti.js which will broadcast
        dispatch('toolbaraction', event.detail);

        if (event.detail.key === 'menu') {
            menuPanelOpen = true;
        }
    }

    /**
     * Returns if area should be hidden from testLayout based on areaHider plugin config
     * @param {string} areaId
     * @returns {boolean}
     */
    function isHiddenArea(areaId) {
        return plugins.areaHider && plugins.areaHider.hiddenAreas && plugins.areaHider.hiddenAreas.includes(areaId);
    }

    // floating tools
    let floatingToolsHeight = 0; // measured from element

    let windowWidth;
    let windowHeight;
    $: setScreenSize(windowWidth);

    $: headerBarHidden = $status === testSessionStatus.overlay && $screenSize.mobile;

    // innerHeight from svelte always return value in px. It creates situation where real test runner height differs from innerHeight after zooming/resizing. To avoid scrollbars, test runner height decreased on 1px.
    $: safeWindowHeight = windowHeight && windowHeight - 1;

    $: modalFeedbackIsShown = $navigationFeedbacksStore && navigationFeedbacksStore.isAnyShown();
    $: if (modalFeedbackIsShown) {
        menuPanelOpen = false;
    }

    let logo = (theme && theme.logo) || {
        src: 'logo.svg',
        alt: __('TAO Logo')
    };

    $: isItemModalFeedback = $testStateStore && isItemModalFeedbackState(testStateStore.getTestContext());

    onMount(() => {
        /**
         * @event mount
         * @param {Object} areas - refs to main areas elements
         */
        setTimeout(() => dispatch('mount', { areas }), 0);
    });
</script>

<style>
    /* local vars */
    :root {
        --testrunner-header-height: 8rem;
        --testrunner-footer-height: 10rem;
        --testrunner-item-top-padding: 0rem;
        --testrunner-item-bottom-padding: 0rem;
        --testrunner-item-max-width: 170.75rem;
        --testrunner-item-vertical-writing-min-margin: 9rem; /* .writing-mode-vertical-rl item: 1.5rem for scroll-shadow, 8rem for bookmark icon */
    }
    .use-a11y-text-styles {
        font-size: var(--fontsize-body);
        line-height: var(--line-height-default);
        letter-spacing: var(--letter-spacing, 0);
        word-spacing: var(--word-spacing, 0);
    }
    .not-displayed {
        display: none;
    }
    .test-runner {
        &.with-item-hanger {
            --testrunner-item-top-padding: 2rem;
            &.with-floating-tools {
                --testrunner-item-bottom-padding: 2rem;
            }
        }
        &.with-floating-tools {
            --testrunner-item-top-padding: var(--floating-tools-height, 5.5rem);
        }

        @media screen and (--mq-maxwidth-medium) {
            --testrunner-header-height: 6rem;
            --testrunner-footer-height: 9rem;
        }
        /* this variable will be passed to itemRunner */
        --testrunner-item-container-height: calc(
            var(--window-height) - var(--testrunner-header-height) - var(--testrunner-footer-height) -
                var(--testrunner-item-top-padding) - var(--testrunner-item-bottom-padding)
        );
        --testrunner-item-container-width: min(var(--testrunner-item-max-width), 100vw);
        --testrunner-item-container-offset-right: calc((100vw - var(--testrunner-item-container-width)) / 2 - 0.125rem); /* centered horizontally */
    }
    .test-runner {
        height: 100%;
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        overscroll-behavior: none;
        color: var(--color-text-default);
    }

    .top-bar {
        z-index: var(--layer-4);
        position: relative;
    }
    .bottom-bar {
        z-index: var(--layer-4);
    }
    .test-runner :global(.overlay) {
        z-index: var(--layer-4);
    }
    .top-bar.overlay {
        z-index: calc(var(--layer-4) + 1);
    }
    .top-bar {
        & :global(img) {
            max-height: calc(var(--testrunner-header-height) - 2rem);
        }
        & :global(aside) {
            display: flex;
        }
    }
    .item-modalfeedback-nav {
        z-index: var(--layer-4);
        height: var(--testrunner-footer-height);
    }

    main {
        height: 100%;
        position: relative;
        overflow: hidden auto;
        background: var(--color-bg-default);
    }
    .qti-item-container {
        min-height: 100%;
        padding-top: var(--testrunner-item-top-padding);
        padding-bottom: var(--testrunner-item-bottom-padding);
        position: relative;

        @media screen and (--mq-minwidth-huge) {
            max-width: var(--testrunner-item-max-width);
            margin: 0 auto;
            height: 100%;
        }
        &:empty {
            padding: 0;
        }
    }

    .panel-content {
        height: 100%;
    }

    .header-bar-content {
        width: 100%;
        display: flex;

        & :global(h1) {
            order: 1;
            overflow: hidden; /* so it can shrink horizontally and get ellipsis */
        }
        & :global(h1 ~ .livesave) {
            order: 2;
            padding-inline-start: 2rem;
        }
    }

    /* reduce space taken by LiveSaveIndicator */
    @define-mixin hide-live-save-indicator {
        & :global(h1 ~ .livesave) {
            min-width: 4rem;
            & :global(.status) {
                display: none;
            }
        }
    }
    @media screen and (--mq-maxwidth-medium) {
        .header-bar-content {
            @add-mixin hide-live-save-indicator;
        }
    }
    :global(body[data-zoom-level='150']),
    :global(body[data-zoom-level='175']),
    :global(body[data-zoom-level='200']) {
        & .header-bar-content {
            @add-mixin hide-live-save-indicator;
        }
    }

    .hidden-item-container {
        position: absolute;
        z-index: var(--layer-back);
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        overflow: visible;
    }

    .transition-wrapper {
        display: contents;
    }

    .floating-toolbars-wrapper {
        pointer-events: none; /* it may cover interactive elements */
        position: absolute;
        inset-inline-end: 0;
        top: 100%;
        margin: 0 4rem; /* should not cover item scrollbar */
        @media screen and (--mq-maxwidth-small) {
            margin: 0 1rem;
        }
    }

    /* override NotificationContainer's default positioning */
    .notification-container-wrapper {
        pointer-events: none; /* it may cover interactive elements */
        position: absolute;
        width: 100%;
        inset-inline-end: 0;
        top: calc(var(--testrunner-header-height) + var(--testrunner-item-top-padding));
        margin: 0 3rem; /* should not cover item scrollbar */
        z-index: calc(var(--layer-5) + 1);
        @media screen and (--mq-maxwidth-small) {
            margin: 0;
        }
        & :global(.notification-wrapper) {
            pointer-events: initial;
        }
    }

    :global(.modal-wrapper.modal-wrapper) {
        z-index: var(--layer-5);
    }

    /* see 'bookletExport' plugin */
    :global(body.booklet-export-mode) {
        & .qti-item-container {
            width: var(--testrunner-item-max-width);
        }
        & main {
            overflow-x: auto;
        }
    }

    /* see item-runner */
    :global(body.item-writing-mode-vertical-rl) {
        --testrunner-item-max-width: calc(100vw - 2 * var(--testrunner-item-vertical-writing-min-margin));
        & .qti-item-container {
            margin: 0 var(--testrunner-item-vertical-writing-min-margin);
        }
    }

    @media print {
        .test-runner,
        main,
        .qti-item-container {
            height: auto;
            overflow: visible;
        }
        .test-runner {
            display: block; /* seems better for Firefox in some cases */
        }
        .feedback-dialogs-wrapper, /* Safari, x-tao-printable, Ctrl+P, pauseOnBlur+forceFullscreen: security modal will cut item content in the middle */
        .test-runner :global(.draggable-modal),
        .test-runner :global(.not-printable) {
            display: none !important;
        }
    }
</style>

<!-- TODO: this should be the primary place in the app where screen dimensions are measured, and set to the store.
Other instances of bind:inner* could be replaced by the screenSizeStore, if it is available. -->
<svelte:window bind:innerWidth={windowWidth} bind:innerHeight={windowHeight} />

<div
    class="test-runner use-a11y-text-styles {$status || testSessionStatus.initial}"
    class:with-item-hanger={itemHangerMessages.length}
    class:with-floating-tools={floatingToolsHeight > 0}
    style="
        --window-height:{safeWindowHeight ? `${safeWindowHeight}px` : '100vh'};
        --floating-tools-height:{floatingToolsHeight}px;
    "
    aria-hidden={modalFeedbackIsShown ? true : void 0}
    bind:this={areas.testRunner}
    class:not-displayed={isHiddenArea('testRunner')}>
    <div bind:this={areas.jumpMenu} class:not-displayed={isHiddenArea('jumpMenu')} />
    <div
        id="test-top-bar"
        class="top-bar not-printable"
        class:overlay={$status === testSessionStatus.overlay}
        class:shadow-bottom={!fullyScrolledUp}
        class:hidden={$status === testSessionStatus.proctorwait}
        bind:this={areas.topBar}
        class:not-displayed={isHiddenArea('topBar')}>
        <div class:hidden={headerBarHidden}>
            <HeaderBar
                on:action={handleHeaderBarAction}
                {startActions}
                {endActions}
                maxEndActions={$screenSize.mobile ? 4 : 9}
                logoSrc={logo.src}
                logoAltText={logo.alt}>
                <div class="header-bar-content" bind:this={areas.header} class:not-displayed={isHiddenArea('header')} />
                <div slot="aside-after">
                    {#if showUserMenu}
                        <UserMenu {testTaker} />
                    {/if}
                </div>
            </HeaderBar>
        </div>
        <div
            class="floating-toolbars-wrapper"
            bind:this={areas.tools}
            class:not-displayed={isHiddenArea('tools')}
            use:resizeObserve
            on:resized={e => (floatingToolsHeight = e.detail.height)}>
            <FloatingToolbarAreas {serviceCallId} {plugins} />
        </div>
    </div>

    <!-- This should be the only NotificationContainer rendered in the app.
    Its API methods allow any code to add or remove Notifications here. -->
    <div class="notification-container-wrapper not-printable">
        <NotificationContainer />
    </div>

    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
    <main
        id="test-main"
        aria-labelledby="a11y-main"
        role={$status === testSessionStatus.overlay ? 'none' : 'main'}
        tabindex={$status === testSessionStatus.overlay ? '-1' : '0'}
        aria-hidden={$status === testSessionStatus.overlay ? 'true' : void 0}
        bind:this={areas.main}
        class:not-displayed={isHiddenArea('main')}>
        <div
            class="scroll-first-child"
            use:visibilityObserver={areas.main || void 0}
            on:isVisible={e => (fullyScrolledUp = e.detail)} />
        <!-- svelte-ignore a11y-missing-content -->
        <h2 id="a11y-main" class="visually-hidden" tabindex="-1" />
        <!-- tabindex because it should be programmatically focusable -->

        <div
            class="qti-item-container"
            class:hidden-item-container={$status === testSessionStatus.loading ||
                $status === testSessionStatus.proctorwait}
            bind:this={areas.content}
            class:not-displayed={isHiddenArea('content')}
            aria-hidden={$status === testSessionStatus.loading ? 'true' : void 0} />

        <div class="transition-wrapper">
            {#if $status === testSessionStatus.loading}
                <Transition text={__('loading')} subtext={__('Please wait a little longer while your content loads')} />
            {/if}
        </div>

        <div
            class="scroll-last-child"
            use:visibilityObserver={areas.main || void 0}
            on:isVisible={e => (fullyScrolledDown = e.detail)} />
    </main>

    <nav
        id="test-navigation"
        class="bottom-bar do-not-read not-printable"
        class:shadow-top={!fullyScrolledDown}
        class:hidden={$status === testSessionStatus.proctorwait || isItemModalFeedback}
        class:not-displayed={isHiddenArea('navigation')}
        aria-hidden="true"
        bind:this={areas.navigation}>
        <h2 class="visually-hidden">{__('Test Navigation')}</h2>
    </nav>

    <nav
        class="item-modalfeedback-nav"
        class:hidden={!isItemModalFeedback ||
            $status === testSessionStatus.proctorwait ||
            $status === testSessionStatus.overlay}
        class:shadow-top={!fullyScrolledDown}
        style="--nav-height: var(--testrunner-footer-height)"
        bind:this={areas.itemModalFeedbackNavigator} />

    <div class="item-hanger-wrapper not-printable">
        {#if itemHangerMessages && itemHangerMessages.length}
            <ItemHanger messages={itemHangerMessages} bottom={floatingToolsHeight > 0} />
        {/if}
    </div>

    <Overlay open={$status === testSessionStatus.overlay}>
        <div slot="header" bind:this={areas.overlayHeader} class:not-displayed={isHiddenArea('overlayHeader')} />
        <div slot="content" bind:this={areas.overlayContent} class:not-displayed={isHiddenArea('overlayContent')} />
        <div slot="footer" bind:this={areas.overlayFooter} class:not-displayed={isHiddenArea('overlayFooter')} />
    </Overlay>
</div>

<!-- the following areas are outside areas.testRunner -->

<Panel bind:open={menuPanelOpen} id={panelId} side="start">
    <div
        class="panel-content use-a11y-text-styles"
        bind:this={areas.panel}
        class:not-displayed={isHiddenArea('panel')} />
</Panel>

<div class="panel-wrapper use-a11y-text-styles" bind:this={areas.a11yMenuPanel} id={a11yPanelId} />

<div class="feedback-dialogs-wrapper use-a11y-text-styles" bind:this={areas.feedbackDialogs}>
    <FeedbackDialogsContainer {serviceCallId} />
</div>
